// MaturaRootSumClosedTask.js
// Zadanie maturalne zamknięte: suma / różnica dwóch pierwiastków sześciennych,
// np. ∛24 + ∛192, po uproszczeniu w postaci c * √[k]{d}.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// d – „część pod pierwiastkiem”, aCoeff, bCoeff – współczynniki po wyciągnięciu
// A = aCoeff^k * d, B = bCoeff^k * d
const CUBEFREE = [2, 3, 5, 6, 7, 10, 11];   // brak sześcianu >1 (8, 27 itd.)

function primeFactorization(number) {
  const factors = [];
  let remaining = number;

  for (let prime = 2; prime * prime <= remaining; prime += 1) {
    while (remaining % prime === 0) {
      factors.push(prime);
      remaining /= prime;
    }
  }
  if (remaining > 1) factors.push(remaining);
  return factors;
}

function primeFactorLatex(number) {
  return primeFactorization(number).join("\\cdot ");
}

function primeFactorLadderLatex(number) {
  let remaining = number;
  const rows = primeFactorization(number).map((prime) => {
    const row = `${remaining} & ${prime} \\\\`;
    remaining /= prime;
    return row;
  });
  rows.push("1 & \\\\");
  return `\\begin{array}{r|l}
${rows.join("\n")}
\\end{array}`;
}

function rootLatex(k, radicand) {
  if (k === 2) return `\\sqrt{${radicand}}`;
  return `\\sqrt[${k}]{${radicand}}`;
}

function coeffTimesRootLatex(k, coeff, d) {
  const inner = rootLatex(k, d);
  if (coeff === 1) return inner;
  return `${coeff}${inner}`;
}

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 3:
 *   ∛24 + ∛192  =  2∛3 + 4∛3  →  C. 6∛3
 */
function createOriginalTask() {
  const k = 3;
  const d = 3;
  const aCoeff = 2;
  const bCoeff = 4;
  const A = 24;
  const B = 192;
  const op = "+";
  const resultCoeff = 6;
  const exprLatex = "\\sqrt[3]{24} + \\sqrt[3]{192}";

  const options = [
    { label: "A", latex: "6", isCorrect: false },
    { label: "B", latex: "3\\sqrt[3]{6}", isCorrect: false },
    { label: "C", latex: "6\\sqrt[3]{3}", isCorrect: true },
    { label: "D", latex: "6\\sqrt[3]{6}", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaRootSumClosedTask",
    data: {
      k,
      d,
      aCoeff,
      bCoeff,
      A,
      B,
      op,
      resultCoeff,
      exprLatex,
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 3,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let k;          // stopień pierwiastka: 2 lub 3
  let d;          // część „pod pierwiastkiem” po uproszczeniu
  let aCoeff;     // współczynnik pierwszego pierwiastka po uproszczeniu
  let bCoeff;     // współczynnik drugiego
  let op;         // "+" lub "-"
  let A, B;       // liczby pod pierwiastkami
  let resultCoeff;

  let tries = 0;

  while (tries < 200) {
    tries += 1;

    k = 3;
    d = pick(CUBEFREE);

    // zakres współczynników – nie za duże, ale >0
    const maxCoeff = 4;
    const coeffs = Array.from({ length: maxCoeff - 1 }, (_, i) => i + 2); // 2..max

    aCoeff = pick(coeffs);
    bCoeff = pick(coeffs);
    if (aCoeff === bCoeff) continue;

    // losujemy plus / minus
    op = pick(["+", "-"]);

    if (op === "-") {
      // chcemy dodatni wynik i niezerowy
      if (aCoeff < bCoeff) [aCoeff, bCoeff] = [bCoeff, aCoeff];
    }

    resultCoeff = op === "+" ? aCoeff + bCoeff : aCoeff - bCoeff;
    if (resultCoeff <= 0) continue;

    // unikamy trywialnych wyników typu 1 * √d
    if (resultCoeff === 1) continue;

    // budujemy pierwiastki
    A = Math.pow(aCoeff, k) * d;
    B = Math.pow(bCoeff, k) * d;

    // trochę kontroli wielkości, żeby liczby nie były ogromne
    if (A > 600 || B > 600) continue;

    break;
  }

  const signLatex = op === "-" ? " - " : " + ";
  const exprLatex = `${rootLatex(k, A)}${signLatex}${rootLatex(k, B)}`;

  const correctLatex = coeffTimesRootLatex(k, resultCoeff, d);

  // --- BŁĘDNE ODPOWIEDZI (distraktory) ---

  const wrongLatexSet = new Set();

  // 1) zgubienie pierwiastka → sama liczba
  wrongLatexSet.add(`${resultCoeff}`);

  // 2) wzięcie tylko jednego składnika (np. tylko pierwszy)
  wrongLatexSet.add(coeffTimesRootLatex(k, aCoeff, d));

  // 3) zły czynnik pod pierwiastkiem, ale dobry współczynnik
  const allDs = CUBEFREE.filter((x) => x !== d);
  if (allDs.length > 0) {
    const wrongD = pick(allDs);
    wrongLatexSet.add(coeffTimesRootLatex(k, resultCoeff, wrongD));
  }

  // 4) awaryjnie – jeszcze jedna prosta pomyłka: suma współczynników, ale tylko jeden pierwiastek
  if (wrongLatexSet.size < 3) {
    wrongLatexSet.add(coeffTimesRootLatex(k, aCoeff + bCoeff, d));
  }

  // usuwamy poprawną odpowiedź z błędnych
  wrongLatexSet.delete(correctLatex);

  const wrongLatex = Array.from(wrongLatexSet).slice(0, 3);

  // składamy opcje
  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    ...wrongLatex.map((lt) => ({ latex: lt, isCorrect: false })),
  ];

  // tasujemy
  const shuffled = optionsRaw
    .map((o) => ({ ...o, sortKey: Math.random() }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((o, idx) => ({
      label: LETTERS[idx],
      latex: o.latex,
      isCorrect: o.isCorrect,
    }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaRootSumClosedTask",
    data: {
      k,
      d,
      aCoeff,
      bCoeff,
      A,
      B,
      op,
      resultCoeff,
      exprLatex,
      options: shuffled,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_3" },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Liczba <InlineMath math={data.exprLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];

  // bez dodatkowego napisu, sam wzór
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { k, d, aCoeff, bCoeff, A, B, op, resultCoeff } = data;

  const rootA = rootLatex(k, A);
  const rootB = rootLatex(k, B);

  const signText = op === "+" ? "sumujemy" : "odejmujemy";

  const factorLineA = `${A}=${primeFactorLatex(A)}`;
  const factorLineB = `${B}=${primeFactorLatex(B)}`;
  const groupedLineA = `${rootA}= ${rootLatex(
    k,
    `${aCoeff}^{${k}}\\cdot ${d}`
  )}= ${coeffTimesRootLatex(k, aCoeff, d)}`;
  const groupedLineB = `${rootB}= ${rootLatex(
    k,
    `${bCoeff}^{${k}}\\cdot ${d}`
  )}= ${coeffTimesRootLatex(k, bCoeff, d)}`;

  const combined = `${coeffTimesRootLatex(
    k,
    aCoeff,
    d
  )} ${op} ${coeffTimesRootLatex(k, bCoeff, d)} = ${coeffTimesRootLatex(
    k,
    resultCoeff,
    d
  )}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Rozkładamy obie liczby pod pierwiastkami na
        czynniki pierwsze:
      </div>
      <div style={{ display: "flex", gap: 32, flexWrap: "wrap", justifyContent: "center" }}>
        <BlockMath math={primeFactorLadderLatex(A)} />
        <BlockMath math={primeFactorLadderLatex(B)} />
      </div>
      <BlockMath math={factorLineA} />
      <BlockMath math={factorLineB} />

      <div>
        <strong>Krok 2.</strong> Łączymy jednakowe czynniki w trójki. Każda
        pełna trójka daje jeden czynnik przed pierwiastkiem sześciennym:
      </div>
      <BlockMath math={groupedLineA} />
      <BlockMath math={groupedLineB} />

      <div>
        <strong>Krok 3.</strong> Otrzymujemy dwa wyrazy podobne:
      </div>
      <BlockMath
        math={`${rootA} ${op} ${rootB} = ${coeffTimesRootLatex(
          k,
          aCoeff,
          d
        )} ${op} ${coeffTimesRootLatex(k, bCoeff, d)}`}
      />

      <div>
        <strong>Krok 4.</strong> {signText} współczynniki przy tym samym
        pierwiastku:
      </div>
      <BlockMath math={combined} />

      <div>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={coeffTimesRootLatex(k, resultCoeff, d)} />
      </div>
    </div>
  );
}
